import { useState, useMemo, useRef } from "react";
import { fmtMoney, fmtPct } from "../parsers/xmlParser";
import { CATEGORY_STYLE } from "../utils/fieldMaps";
import { computeHonKitzba } from "../utils/honKitzba";
import { sourceLogo } from "../utils/dataSource";
import { StatusBadge, CategoryBadge, CompanyLogo } from "./UI";
import ExpandedPanel from "./FundsTableExpanded";
import FundsMobileList from "./FundsMobileList";
import { useIsMobile } from "../hooks/useIsMobile";
import { useFundNotesStore, getFundUniqueKey } from "../hooks/useFundNotesStore";
import { useSnapshotStore } from "../hooks/useSnapshotStore";

// ── Base cell style ──────────────────────────────────────
const cell = {
  padding: "10px 8px", whiteSpace: "nowrap", overflow: "hidden",
  textOverflow: "ellipsis", fontSize: 12, textAlign: "center",
};

function computeRow(f) {
  const redeem     = f.redeem || 0;
  const tsua       = f.returns?.neto || f.returns?.bruto1 || f.returns?.bruto2 || null;
  const dmeiTzvPct = f.dmeiNihul?.actual?.dmeiTzvPct || null;
  const dmeiHafPct = f.dmeiNihul?.actual?.dmeiHafPct || null;
  const hasDebt    = !!f.debts;
  const hasShiabud = f.hasShiabud;
  const hasLoan    = !!f.loan;
  const hk         = computeHonKitzba(f);
  const _T         = new Set(["2", "3", "4", "10"]);
  let sumPitzuyim = 0, sumTagmulim = 0;
  (f.yitrot?.perPeriod || []).forEach(p => {
    const amt = p.amount || 0;
    if (_T.has(p.rekiv)) sumTagmulim += amt;
    else if (p.rekiv === "1") sumPitzuyim += amt;
  });

  let sumLastDeposit = 0;
  if (f.lastDeposit) {
    f.lastDeposit.forEach(d => {
      if (d.isRecurring && d.total) {
        sumLastDeposit += d.total;
      }
    });
  }

  return {
    redeem, tsua, dmeiTzvPct, dmeiHafPct,
    hasDebt, hasShiabud, hasLoan,
    hk, sumPitzuyim, sumTagmulim,
    sumLastDeposit,
  };
}

// ── Source Logo Component ────────────────────────────────
function SourceLogo({ sourceType }) {
  const { src, label, fallback } = sourceLogo(sourceType);
  const [err, setErr] = useState(false);
  
  return (
    <span
      title={`מקור הקובץ: ${label}`}
      style={{ width: 22, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
    >
      {err
        ? <span style={{ fontSize: 15, lineHeight: 1 }}>{fallback}</span>
        : <img src={src} alt={label} onError={() => setErr(true)}
               style={{ width: 18, height: 18, objectFit: "contain", borderRadius: 4 }} />}
    </span>
  );
}

function InlineNoteEditor({ noteKey, initialVal, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [val, setVal] = useState(initialVal || "");

  if (isEditing) {
    return (
      <input
        autoFocus
        value={val}
        onClick={e => e.stopPropagation()}
        onChange={e => setVal(e.target.value)}
        onBlur={() => {
          setIsEditing(false);
          if (val !== (initialVal || "")) onSave(noteKey, val);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            setIsEditing(false);
            if (val !== (initialVal || "")) onSave(noteKey, val);
          }
          if (e.key === 'Escape') {
            setVal(initialVal || "");
            setIsEditing(false);
          }
        }}
        style={{
          width: "100%", padding: "2px 4px", fontSize: 11,
          border: "1px solid #7c3aed", borderRadius: 4, outline: "none",
          background: "#f8fafc", color: "#1e293b", fontFamily: "inherit"
        }}
      />
    );
  }

  return (
    <div 
      style={{
        width: "100%", minHeight: 18, whiteSpace: "nowrap", overflow: "hidden", 
        textOverflow: "ellipsis", textAlign: "right", cursor: "text", 
        color: initialVal ? "#1e293b" : "#cbd5e1", fontSize: 11
      }}
      onClick={(e) => {
        e.stopPropagation();
        setVal(initialVal || "");
        setIsEditing(true);
      }}
      title={initialVal || "לחץ להוספת הערה"}
    >
      {initialVal || "הוסף..."}
    </div>
  );
}

// ── COLUMNS definition ───────────────────────────────────
const COLUMNS = [
  {
    id: "source", label: "מקור", defaultW: 46,
    render: (f) => <div style={{ ...cell, padding: "10px 4px" }}><SourceLogo sourceType={f._sourceType} /></div>,
  },
  {
    id: "cat", label: "קטגוריה", defaultW: 80,
    render: (f) => <div style={{ ...cell }}><CategoryBadge cat={f.cat} /></div>,
  },
  {
    id: "company", label: "חברה", defaultW: 70, align: "right",
    render: (f) => <div style={{ ...cell, textAlign: "right", color: "#64748b" }}><CompanyLogo name={f.companyName} /></div>,
  },
  {
    id: "product", label: "מוצר", defaultW: 150, align: "right",
    render: (f) => (
      <div style={{ ...cell, textAlign: "right", fontWeight: 600, color: "#1e293b" }}>
        {f.tochnit || f.mugName || "—"}
      </div>
    ),
  },
  {
    id: "polNum", label: "חשבון", defaultW: 95, align: "right",
    render: (f, _c, cs) => (
      <div style={{ ...cell, textAlign: "right", color: cs.accent, fontSize: 12, fontWeight: 600 }}>
        {f.polNum || ""}
      </div>
    ),
  },
  {
    id: "maslul", label: "מסלול השקעה", defaultW: 130, align: "right",
    render: (f) => {
      const maslulim = f.maslulim || [];
      const text = maslulim.length > 0 ? maslulim.map(m => m.name).join(", ") : "—";
      return (
        <div 
          style={{ ...cell, textAlign: "right", color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} 
          title={text !== "—" ? text : undefined}
        >
          {text}
        </div>
      );
    },
  },
  {
    id: "status", label: "סטטוס", defaultW: 75,
    render: (f, _c, _cs, flags) => (
      <div style={{ ...cell }}>
        <StatusBadge label={f.statusLabel} />
        {flags.length > 0 && (
          <span style={{ marginRight: 4, display: "inline-flex", alignItems: "center" }}>
            {flags.map((fl, i) => (
              <span key={i} style={{ 
                color: fl === "190" ? "#dc2626" : "inherit", 
                fontWeight: fl === "190" ? 800 : "normal", 
                fontSize: fl === "190" ? 11 : 14,
                marginRight: i > 0 ? 3 : 0,
                lineHeight: 1
              }}>
                {fl}
              </span>
            ))}
          </span>
        )}
      </div>
    ),
  },
  {
    id: "redeem", label: "צבירה", defaultW: 85,
    render: (f, c, cs) => (
      <div style={{ ...cell, fontWeight: 800, fontSize: 14, color: cs.accent }}>
        {c.redeem > 0
          ? fmtMoney(c.redeem)
          : f.premium > 0
            ? <span style={{ fontSize: 11, fontWeight: 700 }}>פרמיה {fmtMoney(f.premium)}<span style={{ fontSize: 9, color: "#94a3b8" }}>/ח׳</span></span>
            : ""}
      </div>
    ),
  },
  {
    id: "firstJoinDate", label: "הצטרפות ראשונה", defaultW: 95,
    render: (f) => (
      <div style={{ ...cell, fontSize: 11, color: "#64748b" }}>{f.firstJoinDate || f.joinDate || ""}</div>
    ),
  },
  {
    id: "validDate", label: "תאריך נכונות", defaultW: 85,
    render: (f) => (
      <div style={{ ...cell, fontSize: 11, color: "#64748b" }}>{f.validDate || ""}</div>
    ),
  },
  {
    id: "notes", label: "הערות", defaultW: 100, align: "right",
    render: (f, _c, _cs, _flags, ctx) => {
      const key = getFundUniqueKey(ctx?.clientId, f);
      const val = key ? ctx?.notes[key] : "";
      return (
        <div style={{ ...cell, width: "100%" }}>
          <InlineNoteEditor 
            noteKey={key} 
            initialVal={val} 
            onSave={(k, v) => { if (k) ctx.setNote(k, v); }} 
          />
        </div>
      );
    }
  },
  {
    id: "advisorRec", label: "המלצת יועץ", defaultW: 120, align: "right",
    render: (f, _c, _cs, _flags, ctx) => {
      const val = ctx?.snapshot?.fundComments?.[f.id] || "";
      return (
        <div style={{ ...cell, width: "100%" }}>
          <InlineNoteEditor 
            noteKey={f.id} 
            initialVal={val} 
            onSave={(k, v) => { if (k) ctx.updateFundComment(k, v); }} 
          />
        </div>
      );
    }
  },
  {
    id: "sugTochnit", label: "תוכנית", defaultW: 50,
    render: (f) => (
      <div style={{ ...cell, textAlign: "center", fontSize: 10, color: "#64748b" }}>
        {f.employment?.sugTochnit || ""}
      </div>
    ),
  },
  {
    id: "agent", label: "סוכן", defaultW: 80, align: "right",
    render: (f) => (
      <div style={{ ...cell, textAlign: "right", fontSize: 11, color: "#2563eb" }}>
        {f.agent?.name || ""}
      </div>
    ),
  },
  {
    id: "tagmulim", label: "תגמולים", defaultW: 80,
    render: (_f, c) => (
      <div style={{ ...cell, fontWeight: 600 }}>
        {c.sumTagmulim > 0 ? fmtMoney(c.sumTagmulim) : ""}
      </div>
    ),
  },
  {
    id: "hon", label: "תגמ' הון", defaultW: 65,
    render: (_f, c) => (
      <div style={{ ...cell, fontSize: 11, color: "#475569" }}>
        {c.hk.hon > 0 ? fmtMoney(c.hk.hon) : ""}
      </div>
    ),
  },
  {
    id: "kitzba", label: "תגמ' קצבה", defaultW: 65,
    render: (_f, c) => (
      <div style={{ ...cell, fontSize: 11, color: "#475569" }}>
        {c.hk.kitzba > 0 ? fmtMoney(c.hk.kitzba) : ""}
      </div>
    ),
  },
  {
    id: "pitzuyim", label: "פיצויים", defaultW: 80,
    render: (_f, c) => (
      <div style={{ ...cell, fontWeight: 600 }}>
        {c.sumPitzuyim > 0 ? fmtMoney(c.sumPitzuyim) : ""}
      </div>
    ),
  },
  {
    id: "honPitzuyim", label: "פיצ' הון", defaultW: 65,
    render: (_f, c) => (
      <div style={{ ...cell, fontSize: 11, color: "#475569" }}>
        {c.hk.honPitzuyim > 0 ? fmtMoney(c.hk.honPitzuyim) : ""}
      </div>
    ),
  },
  {
    id: "kitzbaPitz", label: "פיצ' קצבה", defaultW: 65,
    render: (_f, c) => (
      <div style={{ ...cell, fontSize: 11, color: "#475569" }}>
        {c.hk.kitzbaPitzuyim > 0 ? fmtMoney(c.hk.kitzbaPitzuyim) : ""}
      </div>
    ),
  },
  {
    id: "dmeiTzv", label: "ד.נ.צבירה", defaultW: 60,
    render: (_f, c) => (
      <div style={{ ...cell, color: "#475569" }}>
        {c.dmeiTzvPct ? fmtPct(c.dmeiTzvPct) : ""}
      </div>
    ),
  },
  {
    id: "dmeiHaf", label: "ד.נ.הפקדה", defaultW: 60,
    render: (_f, c) => (
      <div style={{ ...cell, color: "#475569" }}>
        {c.dmeiHafPct ? fmtPct(c.dmeiHafPct) : ""}
      </div>
    ),
  },
  {
    id: "tsua", label: "תשואה", defaultW: 60,
    render: (_f, c) => (
      <div style={{ ...cell, color: "#16a34a" }}>
        {c.tsua != null ? fmtPct(c.tsua) : ""}
      </div>
    ),
  },
  {
    id: "lastDeposit", label: "הפקדה אחרונה", defaultW: 85,
    render: (_f, c) => (
      <div style={{ ...cell, fontWeight: 600, color: "#1e293b" }}>
        {c.sumLastDeposit > 0 ? fmtMoney(c.sumLastDeposit) : ""}
      </div>
    ),
  },
];

const ACTION_COL_WIDTH = "70px";
const BY_ID        = Object.fromEntries(COLUMNS.map(col => [col.id, col]));
const DEFAULT_ORDER = COLUMNS.map(c => c.id);
const DEFAULT_WIDTHS = Object.fromEntries(COLUMNS.map(c => [c.id, c.defaultW]));

const LS_KEY          = "fundsTableColOrder_v2";
const LS_KEY_WIDTHS   = "fundsTableColWidths";
const LS_KEY_HIDDEN   = "fundsTableColHidden";
const LS_KEY_CATS     = "fundsTableCatFilter";

const CAT_ORDER_DISPLAY = [
  "קרן פנסיה", "ביטוח מנהלים", "קופת גמל", "קופת גמל להשקעה", "קרן השתלמות", "פרט", "תיק מנוהל",
];

// ── Main Table ──────────────────────────────────────────
export default function FundsTable({ funds, clientId, totalCount, activeTab, onAddProduct, onRemoveFund }) {
  const containerRef = useRef(null);
  const isMobile = useIsMobile();
  const { notes, setNote } = useFundNotesStore();
  const { snapshot, updateFundComment } = useSnapshotStore(clientId);
  const ctx = { notes, setNote, snapshot, updateFundComment, clientId };
  
  const [openId, setOpenId]     = useState(null);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [showPicker, setShowPicker]         = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "cat", direction: "asc" });

  const handleSort = (key) => {
    const SORTABLE_KEYS = ["cat", "company", "redeem", "product", "pitzuyim", "tagmulim", "hon", "kitzba", "honPitzuyim", "kitzbaPitz", "lastDeposit"];
    if (!SORTABLE_KEYS.includes(key)) return;
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      const isNumeric = ["redeem", "pitzuyim", "tagmulim", "hon", "kitzba", "honPitzuyim", "kitzbaPitz", "lastDeposit"].includes(key);
      return { key, direction: isNumeric ? "desc" : "asc" };
    });
  };

  const [colOrder, setColOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_KEY));
      if (Array.isArray(saved) && saved.length === DEFAULT_ORDER.length &&
          saved.every(id => BY_ID[id])) return saved;
    } catch {}
    return DEFAULT_ORDER;
  });

  const [colWidths, setColWidths] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_KEY_WIDTHS));
      if (saved && typeof saved === "object") return { ...DEFAULT_WIDTHS, ...saved };
    } catch {}
    return { ...DEFAULT_WIDTHS };
  });

  const [hiddenCols, setHiddenCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_KEY_HIDDEN));
      if (Array.isArray(saved)) return new Set(saved);
    } catch {}
    return new Set();
  });

  const [selectedCats, setSelectedCats] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_KEY_CATS));
      if (Array.isArray(saved)) return new Set(saved);
    } catch {}
    return new Set();
  });

  // Unique categories that actually appear in current funds, in display order
  const allCats = useMemo(() => {
    const present = new Set(funds.map(f => f.cat).filter(Boolean));
    const ordered = CAT_ORDER_DISPLAY.filter(c => present.has(c));
    // append any unexpected categories at the end
    present.forEach(c => { if (!CAT_ORDER_DISPLAY.includes(c)) ordered.push(c); });
    return ordered;
  }, [funds]);

  const rows = useMemo(() => {
    let list = funds.map(f => ({ fund: f, computed: computeRow(f) }));
    if (selectedCats.size > 0)
      list = list.filter(({ fund: f }) => selectedCats.has(f.cat));
    return list.sort(({ fund: a, computed: ca }, { fund: b, computed: cb }) => {
      let result = 0;
      if (sortConfig.key === "cat") {
        const ai = CAT_ORDER_DISPLAY.indexOf(a.cat), bi = CAT_ORDER_DISPLAY.indexOf(b.cat);
        const ao = ai >= 0 ? ai : CAT_ORDER_DISPLAY.length, bo = bi >= 0 ? bi : CAT_ORDER_DISPLAY.length;
        result = ao !== bo ? ao - bo : (b.redeem || 0) - (a.redeem || 0);
      } else if (sortConfig.key === "company") {
        const cA = a.companyName || "";
        const cB = b.companyName || "";
        result = cA.localeCompare(cB, "he");
        if (result === 0) result = (b.redeem || 0) - (a.redeem || 0);
      } else if (sortConfig.key === "product") {
        const pA = a.tochnit || a.mugName || "";
        const pB = b.tochnit || b.mugName || "";
        result = pA.localeCompare(pB, "he");
        if (result === 0) result = (b.redeem || 0) - (a.redeem || 0);
      } else if (sortConfig.key === "redeem") {
        result = (ca.redeem || 0) - (cb.redeem || 0);
      } else if (sortConfig.key === "pitzuyim") {
        result = (ca.sumPitzuyim || 0) - (cb.sumPitzuyim || 0);
      } else if (sortConfig.key === "tagmulim") {
        result = (ca.sumTagmulim || 0) - (cb.sumTagmulim || 0);
      } else if (sortConfig.key === "hon") {
        result = (ca.hk.hon || 0) - (cb.hk.hon || 0);
      } else if (sortConfig.key === "kitzba") {
        result = (ca.hk.kitzba || 0) - (cb.hk.kitzba || 0);
      } else if (sortConfig.key === "honPitzuyim") {
        result = (ca.hk.honPitzuyim || 0) - (cb.hk.honPitzuyim || 0);
      } else if (sortConfig.key === "kitzbaPitz") {
        result = (ca.hk.kitzbaPitzuyim || 0) - (cb.hk.kitzbaPitzuyim || 0);
      } else if (sortConfig.key === "lastDeposit") {
        result = (ca.sumLastDeposit || 0) - (cb.sumLastDeposit || 0);
      }
      return sortConfig.direction === "asc" ? result : -result;
    });
  }, [funds, selectedCats, sortConfig]);

  if (isMobile) return <FundsMobileList rows={rows} clientId={clientId} />;

  const insuranceHidden = new Set(["sugTochnit", "tagmulim", "hon", "kitzba", "pitzuyim", "honPitzuyim", "kitzbaPitz", "dmeiTzv", "dmeiHaf", "tsua"]);

  const visibleCols = colOrder.filter(id => {
    if (hiddenCols.has(id)) return false;
    if (activeTab === "insurance" && insuranceHidden.has(id)) return false;
    return true;
  });
  // The action column stretches to fill remaining table width
  // "redeem" column ensures it doesn't truncate large amounts
  const GRID = [...visibleCols.map(id => {
    const w = colWidths[id] || DEFAULT_WIDTHS[id];
    return id === "redeem" ? `minmax(${w}px, max-content)` : `${w}px`;
  }), `minmax(${ACTION_COL_WIDTH}, 1fr)`].join(" ");

  function handleResizeStart(e, id) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[id] || DEFAULT_WIDTHS[id];

    function onMove(ev) {
      const newW = Math.max(40, startW + (startX - ev.clientX));
      
      // Update DOM directly for smooth 60fps dragging without React re-renders
      if (containerRef.current) {
         const tempWidths = { ...colWidths, [id]: newW };
         const newGrid = [...visibleCols.map(cId => {
           const w = tempWidths[cId] || DEFAULT_WIDTHS[cId];
           return cId === "redeem" ? `minmax(${w}px, max-content)` : `${w}px`;
         }), `minmax(${ACTION_COL_WIDTH}, 1fr)`].join(" ");
         containerRef.current.style.setProperty("--table-grid", newGrid);
      }
    }
    
    function onUp(ev) {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      // Now update React state once
      const finalW = Math.max(40, startW + (startX - ev.clientX));
      setColWidths(prev => {
        const next = { ...prev, [id]: finalW };
        try { localStorage.setItem(LS_KEY_WIDTHS, JSON.stringify(next)); } catch {}
        return next;
      });
    }
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  function toggleCat(name) {
    setSelectedCats(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      try { localStorage.setItem(LS_KEY_CATS, JSON.stringify([...next])); } catch {}
      return next;
    });
  }

  function toggleCol(id) {
    setHiddenCols(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem(LS_KEY_HIDDEN, JSON.stringify([...next])); } catch {}
      return next;
    });
  }

  function handleDrop(targetId) {
    if (!dragFrom || dragFrom === targetId) { setDragFrom(null); setDragOver(null); return; }
    setColOrder(prev => {
      const arr  = [...prev];
      const from = arr.indexOf(dragFrom);
      const to   = arr.indexOf(targetId);
      if (from === -1 || to === -1) return prev;
      arr.splice(from, 1);
      arr.splice(to, 0, dragFrom);
      try { localStorage.setItem(LS_KEY, JSON.stringify(arr)); } catch {}
      return arr;
    });
    setDragFrom(null);
    setDragOver(null);
  }

  // ── Drag to scroll logic ──
  const tableScrollRef = useRef(null);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // Only left click
    if (e.target.closest('button, input, label, a, textarea, [draggable]')) return;
    
    // Ignore clicks on the horizontal scrollbar
    const el = tableScrollRef.current;
    if (el && e.clientY > el.getBoundingClientRect().bottom - 16) return;

    isDragging.current = true;
    hasDragged.current = false;
    startX.current = e.pageX - tableScrollRef.current.offsetLeft;
    scrollLeft.current = tableScrollRef.current.scrollLeft;
    
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  };

  const handleMouseLeave = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  const handleMouseUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - tableScrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    if (Math.abs(walk) > 5) {
      hasDragged.current = true;
    }
    tableScrollRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handleCaptureClick = (e) => {
    if (hasDragged.current) {
      e.stopPropagation();
      e.preventDefault();
      hasDragged.current = false;
    }
  };

  return (
    <div dir="rtl" ref={containerRef} style={{ "--table-grid": GRID }} onClick={() => { setShowPicker(false); setShowProductPicker(false); }}>

      {/* ── Toolbar ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>

        {/* Add manual product — provided only on holdings clients */}
        {onAddProduct && (
          <button
            onClick={e => { e.stopPropagation(); onAddProduct(); }}
            title="הוסף מוצר ידני (פק״מ, תיק בנקאי וכו')"
            style={{
              fontSize: 11, padding: "4px 10px", borderRadius: 8, cursor: "pointer", fontWeight: 700,
              display: "flex", alignItems: "center", gap: 4,
              background: "#ecfeff", color: "#0e7490",
              border: "1.5px solid #0e7490", whiteSpace: "nowrap",
            }}
          >
            ➕ הוסף מוצר
          </button>
        )}

        {/* Category filter */}
        <div style={{ position: "relative" }}>
          <button
            onClick={e => { e.stopPropagation(); setShowProductPicker(p => !p); setShowPicker(false); }}
            style={{
              fontSize: 11, padding: "4px 10px", borderRadius: 8, cursor: "pointer", fontWeight: 600,
              display: "flex", alignItems: "center", gap: 4,
              background: selectedCats.size > 0 ? "#7c3aed" : "#f5f3ff",
              color: selectedCats.size > 0 ? "white" : "#7c3aed",
              border: "1.5px solid #7c3aed",
            }}
          >
            🔍 סינון מוצרים
            {selectedCats.size > 0 && (
              <span style={{
                background: "rgba(255,255,255,0.25)", borderRadius: 10,
                padding: "0 6px", fontSize: 10,
              }}>{selectedCats.size}</span>
            )}
          </button>
          {showProductPicker && (
            <div
              onClick={e => e.stopPropagation()}
              style={{
                position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 200,
                background: "white", border: "1px solid #e2e8f0", borderRadius: 12,
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: "12px 14px",
                minWidth: 220,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>סינון לפי סוג מוצר</span>
                {selectedCats.size > 0 && (
                  <button onClick={() => {
                    setSelectedCats(new Set());
                    try { localStorage.removeItem(LS_KEY_CATS); } catch {}
                  }} style={{ fontSize: 10, color: "#7c3aed", background: "none", border: "none",
                    cursor: "pointer", textDecoration: "underline" }}>
                    נקה הכל
                  </button>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {allCats.map(cat => {
                  const active = selectedCats.has(cat);
                  const cs = CATEGORY_STYLE[cat] || CATEGORY_STYLE["ביטוח מנהלים"];
                  const count = funds.filter(f => f.cat === cat).length;
                  return (
                    <label key={cat} onClick={() => toggleCat(cat)} style={{
                      display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                      fontSize: 12, padding: "6px 8px", borderRadius: 8,
                      background: active ? cs.light : "transparent",
                      border: `1.5px solid ${active ? cs.border : "transparent"}`,
                    }}>
                      <input type="checkbox" checked={active} onChange={() => toggleCat(cat)}
                        style={{ accentColor: cs.accent }} onClick={e => e.stopPropagation()} />
                      <span style={{
                        display: "inline-block", width: 10, height: 10, borderRadius: "50%",
                        background: cs.accent, flexShrink: 0,
                      }} />
                      <span style={{ color: active ? cs.dark : "#1e293b", fontWeight: active ? 700 : 400, flex: 1 }}>
                        {cat}
                      </span>
                      <span style={{ fontSize: 10, color: "#94a3b8" }}>{count}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        </div>

        {/* Column visibility */}
        <div style={{ position: "relative" }}>
          <button
            onClick={e => { e.stopPropagation(); setShowPicker(p => !p); setShowProductPicker(false); }}
            style={{
              fontSize: 11, padding: "4px 10px", borderRadius: 8, cursor: "pointer", fontWeight: 600,
              display: "flex", alignItems: "center", gap: 4,
              background: hiddenCols.size > 0 ? "#7c3aed" : "#f5f3ff",
              color: hiddenCols.size > 0 ? "white" : "#7c3aed",
              border: "1.5px solid #7c3aed",
            }}
          >
            ☰ סינון עמודות
            {hiddenCols.size > 0 && (
              <span style={{
                background: "rgba(255,255,255,0.25)", borderRadius: 10,
                padding: "0 6px", fontSize: 10,
              }}>{COLUMNS.length - hiddenCols.size}/{COLUMNS.length}</span>
            )}
          </button>
          {showPicker && (
            <div
              onClick={e => e.stopPropagation()}
              style={{
                position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 200,
                background: "white", border: "1px solid #e2e8f0", borderRadius: 12,
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: "12px 14px",
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 18px", minWidth: 360,
              }}
            >
              <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "space-between",
                alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>הצגת עמודות</span>
                <button
                  onClick={() => {
                    setColOrder(DEFAULT_ORDER);
                    try { localStorage.removeItem(LS_KEY); } catch {}
                    setHiddenCols(new Set());
                    try { localStorage.removeItem(LS_KEY_HIDDEN); } catch {}
                    setColWidths({ ...DEFAULT_WIDTHS });
                    try { localStorage.removeItem(LS_KEY_WIDTHS); } catch {}
                  }}
                  style={{ fontSize: 10, color: "#64748b", background: "none", border: "none",
                    cursor: "pointer", textDecoration: "underline" }}
                >
                  איפוס
                </button>
              </div>
              {DEFAULT_ORDER.map(id => {
                const col = BY_ID[id];
                const hidden = hiddenCols.has(id);
                return (
                  <label key={id} style={{ display: "flex", alignItems: "center", gap: 6,
                    cursor: "pointer", fontSize: 12, color: hidden ? "#94a3b8" : "#1e293b" }}>
                    <input type="checkbox" checked={!hidden} onChange={() => toggleCol(id)}
                      style={{ accentColor: "#7c3aed" }} />
                    {col.label}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Info text — left side */}
        <div style={{ fontSize: 11, color: "#94a3b8", marginRight: "auto" }}>
          מציג {rows.length}{totalCount != null && totalCount !== rows.length ? ` מתוך ${totalCount}` : ""} מוצרים
          {" · "}לחיצה על שורה לפרטים מלאים
        </div>

      </div>

      {/* ── Table ── */}
      <div 
        ref={tableScrollRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onClickCapture={handleCaptureClick}
        style={{
          background: "white", borderRadius: 14, border: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)", overflowX: "auto", overflowY: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          display: "grid", gridTemplateColumns: "var(--table-grid)", gap: 0,
          background: "#f8fafc", borderBottom: "2px solid #e2e8f0",
        }}>
          {visibleCols.map(id => {
            const col = BY_ID[id];
            const isDragging = dragFrom === id;
            const isOver     = dragOver === id;
            const isSortable = ["cat", "company", "redeem", "product", "pitzuyim", "tagmulim", "hon", "kitzba", "honPitzuyim", "kitzbaPitz"].includes(id);
            return (
              <div
                key={id}
                draggable
                onDragStart={() => { setDragFrom(id); setDragOver(null); }}
                onDragOver={e  => { e.preventDefault(); setDragOver(id); }}
                onDragLeave={() => setDragOver(prev => prev === id ? null : prev)}
                onDrop={() => handleDrop(id)}
                onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
                onClick={(e) => {
                  // Only sort if we didn't drag
                  if (isSortable) handleSort(id);
                }}
                style={{
                  ...cell, position: "relative",
                  textAlign: col.align || "center",
                  overflow: "visible", whiteSpace: "normal", textOverflow: "unset",
                  fontSize: 11, fontWeight: 700, color: "#64748b",
                  cursor: isSortable ? "pointer" : "grab", userSelect: "none",
                  opacity: isDragging ? 0.45 : 1,
                  background: isOver && !isDragging ? "#ede9fe" : undefined,
                  borderLeft: isOver && !isDragging ? "2px solid #4338ca" : "2px solid transparent",
                  transition: "background 0.1s, border-color 0.1s",
                }}
                title={isSortable ? "לחץ למיון, גרור לשינוי מיקום" : "גרור לשינוי מיקום"}
              >
                <span style={{ fontSize: 9, color: "#cbd5e1", marginLeft: 3, letterSpacing: -1, cursor: "grab" }}>⠿</span>
                {col.label}
                {isSortable && sortConfig.key === id && (
                  <span style={{ marginLeft: 4, fontSize: 10, color: "#4f46e5" }}>
                    {sortConfig.direction === "asc" ? "▲" : "▼"}
                  </span>
                )}
                {/* Resize handle — left edge (RTL trailing border) */}
                <div
                  onMouseDown={e => { e.stopPropagation(); handleResizeStart(e, id); }}
                  onClick={e => e.stopPropagation()}
                  style={{
                    position: "absolute", left: 0, top: 0, bottom: 0, width: 6,
                    cursor: "col-resize", zIndex: 2,
                    borderLeft: "2px solid #cbd5e1",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderLeftColor = "#64748b"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderLeftColor = "#cbd5e1"; }}
                  title="גרור לשינוי רוחב"
                />
              </div>
            );
          })}
          {/* action header — not draggable */}
          <div style={{ ...cell, fontSize: 11, fontWeight: 700, color: "#64748b" }} />
        </div>

        {/* Rows */}
        {rows.map(({ fund: f, computed: c }, idx) => {
          const cs     = CATEGORY_STYLE[f.cat] || CATEGORY_STYLE["ביטוח מנהלים"];
          const isOpen = openId === f.id;
          const flags  = [];
          if (f.tikun190 === "כן" && f.cat !== "קרן השתלמות") flags.push("190");
          if (c.hasDebt)    flags.push("⚠️");
          if (c.hasShiabud) flags.push("🔒");
          if (c.hasLoan)    flags.push("💰");

          return (
            <div key={f.id || `fallback_${idx}`}>
              <div
                onClick={() => setOpenId(isOpen ? null : f.id)}
                style={{
                  display: "grid", gridTemplateColumns: "var(--table-grid)", gap: 0,
                  alignItems: "center", cursor: "pointer",
                  borderBottom: isOpen ? "none" : "1px solid #f1f5f9",
                  borderRight: `3px solid ${cs.border || cs.accent}`,
                  background: isOpen ? cs.bg : "white",
                  transition: "background 0.1s",
                }}
              >
                {visibleCols.map(id => {
                  const col = BY_ID[id];
                  return <div key={id}>{col.render(f, c, cs, flags, ctx)}</div>;
                })}
                {/* action cell — always last */}
                <div style={{ ...cell, overflow: "visible", whiteSpace: "nowrap", paddingLeft: 1, display: "flex", gap: 4, alignItems: "center", justifyContent: "flex-end" }}>
                  {onRemoveFund && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm("האם אתה בטוח שברצונך למחוק קרן זו מהתיק? (לא ניתן לביטול)")) {
                          onRemoveFund(clientId, f.id);
                        }
                      }}
                      title="מחק קרן"
                      style={{
                        fontSize: 11, fontWeight: 700, color: "#dc2626",
                        background: "#fee2e2", border: "1px solid #fca5a5",
                        borderRadius: 6, padding: "3px 6px", lineHeight: 1.5,
                        cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
                      }}
                    >
                      🗑️
                    </span>
                  )}
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: cs.accent,
                    background: `${cs.accent}14`, border: `1px solid ${cs.accent}55`,
                    borderRadius: 6, padding: "3px 10px", lineHeight: 1.5,
                    cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
                  }}>
                    {isOpen ? "סגור ▲" : "פירוט ▸"}
                  </span>
                </div>
              </div>

              {isOpen && <ExpandedPanel fund={f} computed={c} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
